import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Search, Plus, Phone, Mail, Building, User, Calendar, Edit2, Trash2, Loader2,
  MessageSquare, ChevronRight, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  nuevo: 'bg-blue-100 text-blue-800',
  contactado: 'bg-purple-100 text-purple-800',
  interesado: 'bg-yellow-100 text-yellow-800',
  propuesta: 'bg-emerald-100 text-emerald-800',
  cerrado: 'bg-green-100 text-green-800',
  perdido: 'bg-red-100 text-red-800'
};

const STAGE_NAMES = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  interesado: 'Interesado',
  propuesta: 'Propuesta',
  cerrado: 'Cerrado',
  perdido: 'Perdido'
};

const STAGES = Object.entries(STAGE_NAMES).map(([id, name]) => ({ id, name }));

const Contacts = () => {
  const { token, user } = useAuth();
  const { notifyNewLead } = useNotifications();
  const [contacts, setContacts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showNewContact, setShowNewContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    notes: '',
    stage: 'nuevo',
    assigned_agent_id: ''
  });

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (stageFilter) params.append('stage', stageFilter);
      
      const response = await fetch(`${API_URL}/api/contacts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchContactMessages = async (contactId) => {
    try {
      // First get the conversation for this contact
      const convResponse = await fetch(`${API_URL}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (convResponse.ok) {
        const conversations = await convResponse.json();
        const conv = conversations.find(c => c.contact_id === contactId);
        if (conv) {
          const msgResponse = await fetch(`${API_URL}/api/conversations/${conv.conversation_id}/messages`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (msgResponse.ok) {
            const data = await msgResponse.json();
            setMessages(data);
          }
        } else {
          setMessages([]);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchTeamMembers();
  }, [token, searchQuery, stageFilter]);

  useEffect(() => {
    if (selectedContact) {
      fetchContactMessages(selectedContact.contact_id);
    }
  }, [selectedContact]);

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      notes: '',
      stage: 'nuevo',
      assigned_agent_id: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingContact 
        ? `${API_URL}/api/contacts/${editingContact.contact_id}`
        : `${API_URL}/api/contacts`;
      
      const response = await fetch(url, {
        method: editingContact ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newContact = await response.json();
        toast.success(editingContact ? 'Contacto actualizado' : 'Contacto creado');
        
        // Notify new lead if it's a new contact with 'nuevo' stage
        if (!editingContact && formData.stage === 'nuevo') {
          notifyNewLead(formData.name, 'Nuevo');
        }
        
        fetchContacts();
        setShowNewContact(false);
        setEditingContact(null);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar contacto');
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error('Error al guardar contacto');
    }
  };

  const handleEdit = (contact) => {
    setFormData({
      name: contact.name,
      phone: contact.phone,
      email: contact.email || '',
      company: contact.company || '',
      notes: contact.notes || '',
      stage: contact.stage,
      assigned_agent_id: contact.assigned_agent_id || ''
    });
    setEditingContact(contact);
    setShowNewContact(true);
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('¿Estás seguro de eliminar este contacto?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Contacto eliminado');
        fetchContacts();
        if (selectedContact?.contact_id === contactId) {
          setSelectedContact(null);
        }
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Error al eliminar contacto');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-[#25D366] animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full" data-testid="contacts-container">
      {/* Contacts List */}
      <div className="w-full md:w-96 border-r border-slate-200 flex flex-col bg-white">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold font-['Outfit'] text-slate-900">Contactos</h2>
            <Dialog open={showNewContact} onOpenChange={(open) => {
              setShowNewContact(open);
              if (!open) {
                setEditingContact(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  size="sm" 
                  data-testid="new-contact-btn"
                  className="bg-[#25D366] hover:bg-[#1DA851]"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nuevo
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingContact ? 'Editar contacto' : 'Nuevo contacto'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Teléfono *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+34612345678"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Empresa</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="stage">Etapa</Label>
                      <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STAGES.map(stage => (
                            <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assigned">Asignar a</Label>
                      <Select 
                        value={formData.assigned_agent_id || "none"} 
                        onValueChange={(v) => setFormData({ ...formData, assigned_agent_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sin asignar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin asignar</SelectItem>
                          {teamMembers.map(member => (
                            <SelectItem key={member.user_id} value={member.user_id}>
                              {member.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notas</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notas adicionales sobre el contacto..."
                    />
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowNewContact(false);
                        setEditingContact(null);
                        resetForm();
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-[#25D366] hover:bg-[#1DA851]">
                      {editingContact ? 'Actualizar' : 'Crear'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar contactos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-contacts"
                className="pl-10"
              />
            </div>
            
            <Select value={stageFilter || "all"} onValueChange={(v) => setStageFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-stage">
                <SelectValue placeholder="Todas las etapas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las etapas</SelectItem>
                {STAGES.map(stage => (
                  <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-2">
            {contacts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay contactos</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.contact_id}
                  data-testid={`contact-${contact.contact_id}`}
                  onClick={() => setSelectedContact(contact)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors mb-1 ${
                    selectedContact?.contact_id === contact.contact_id
                      ? 'bg-slate-100 ring-1 ring-slate-200'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 flex-shrink-0">
                      <AvatarFallback className="bg-[#25D366] text-white text-sm">
                        {contact.name?.charAt(0) || 'C'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-900 truncate">{contact.name}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                      <p className="text-sm text-slate-500 truncate">{contact.phone}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className={`text-xs ${STAGE_COLORS[contact.stage]}`}>
                          {STAGE_NAMES[contact.stage]}
                        </Badge>
                        {contact.company && (
                          <span className="text-xs text-slate-400 truncate">{contact.company}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Contact Details */}
      <div className="hidden md:flex flex-1 flex-col bg-slate-50">
        {selectedContact ? (
          <>
            <div className="p-6 bg-white border-b border-slate-200">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="bg-[#25D366] text-white text-2xl">
                      {selectedContact.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="text-2xl font-bold font-['Outfit'] text-slate-900">
                      {selectedContact.name}
                    </h2>
                    <Badge className={`mt-1 ${STAGE_COLORS[selectedContact.stage]}`}>
                      {STAGE_NAMES[selectedContact.stage]}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(selectedContact)}
                    data-testid="edit-contact-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(selectedContact.contact_id)}
                    data-testid="delete-contact-btn"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Contact Info Card */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold">Información</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                        <Phone className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Teléfono</p>
                        <p className="font-medium">{selectedContact.phone}</p>
                      </div>
                    </div>
                    {selectedContact.email && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <Mail className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="font-medium">{selectedContact.email}</p>
                        </div>
                      </div>
                    )}
                    {selectedContact.company && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <Building className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Empresa</p>
                          <p className="font-medium">{selectedContact.company}</p>
                        </div>
                      </div>
                    )}
                    {selectedContact.assigned_agent_name && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Asignado a</p>
                          <p className="font-medium">{selectedContact.assigned_agent_name}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Creado</p>
                        <p className="font-medium">
                          {format(new Date(selectedContact.created_at), 'dd/MM/yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Notes Card */}
                {selectedContact.notes && (
                  <Card className="border-slate-200">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg font-semibold">Notas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 whitespace-pre-wrap">{selectedContact.notes}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Conversation History */}
                <Card className="border-slate-200 lg:col-span-2">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Historial de conversación
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Sin mensajes</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {messages.map((msg) => (
                          <div
                            key={msg.message_id}
                            className={`flex ${msg.sender_type === 'agent' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] px-4 py-2 rounded-lg ${
                                msg.sender_type === 'agent'
                                  ? 'bg-[#DCF8C6]'
                                  : 'bg-white border border-slate-200'
                              }`}
                            >
                              <p className="text-slate-800">{msg.content}</p>
                              <p className="text-xs text-slate-500 mt-1 text-right">
                                {format(new Date(msg.created_at), 'dd/MM HH:mm')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">Selecciona un contacto</p>
              <p className="text-sm">para ver sus detalles</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contacts;
